import mixpanelBrowser from 'mixpanel-browser'

const ROLE_LABELS = {
    100: 'Super Admin',
    110: 'Master',
    120: 'Divisional',
    130: 'Portfolio',
    140: 'Product',
    150: 'Director',
    170: 'Report',
}

const OID_TYPE_MAP = {
    3: 'company',
    4: 'division',
    5: 'portfolio',
    6: 'product',
}

export const MIXPANEL_EVENTS = {
    // Survey lifecycle
    SURVEY_CREATED: 'Survey Created',
    SURVEY_PUBLISHED: 'Survey Published',
    SURVEY_CLOSED: 'Survey Closed',
    SURVEY_DUPLICATED: 'Survey Duplicated',
    SURVEY_DELETED: 'Survey Deleted',
    SURVEY_EDITED: 'Survey Edited',
    // Survey builder
    SURVEY_BUILDER_OPENED: 'S Builder Opened',
    SURVEY_BUILDER_CLOSED: 'S Builder Closed',
    SURVEY_BUILDER_PAGE_ADDED: 'S Builder Page Added',
    SURVEY_BUILDER_PAGE_MENU_ACTION: 'S Builder Page 3 Dot Menu Action', // { action: string, value?: boolean } value is for toggles and redirect vals
    SURVEY_BUILDER_QUESTION_MENU_ACTION: 'S Builder Question 3 Dot Menu Action', // { action: string, value?: boolean } (excluding edit) value is for toggles
    SURVEY_BUILDER_QUESTION_CREATE_EDIT: 'S Builder Create/Edit Question Action', // {
    // user_action: 'open' | 'close' | 'save',
    // question_action: 'edit' | 'create',
    // question_type?: // optional, only if editing OR saving new question
    // }
    SURVEY_BUILDER_QUESTION_DRAGGED: 'S Builder Question Dragged', // { library: 'explori' | 'company' | 'recommended' | 'mandatory' } 
    SURVEY_BUILDER_QUESTION_MANDATORY_AUTO_ADDED: 'S Builder Mandatory Questions Auto Added',
    SURVEY_BUILDER_LOGIC_EDITED: 'S Builder Logic Edited', // {
    // type: 'page | question',
    // action: 'add/edit' | 'delete',
    // question_type: string (only if type = question),
    // logic_type: 'inheritance' | 'display' | 'routing' | 'option' // page has only display/routing
    // source: 'page move' | 'question move' | 'menu'
    // }
    // Sales Intelligence
    SI_EVENT_LEVEL_OPENED: 'SI Event Level Opened',
    SI_EXHIBITOR_LEVEL_OPENED: 'SI Exhibitor Level Opened',
    SI_EXHIBITOR_FILTER_CHANGE: 'SI Exhibitor Filter Changed',
    // Dashboard
    DASHBOARD_LOADED: 'Dashboard Loaded',
    DASHBOARD_FILTER_OPENED: 'Dashboard Filter Opened',
    DASHBOARD_FILTER_SAVED: 'Dashboard Filter Saved',
    DASHBOARD_FILTER_CLOSED: 'Dashboard Filter Closed',
    DASHBOARD_PINNED_FILTER_CHANGED: 'Dashboard Pinned Filter Changed',
    WIDGET_CREATOR_OPENED: 'Widget Creator Opened',
    WIDGET_CREATOR_SAVED: 'Widget Creator Saved',
    WIDGET_CREATOR_CLOSED: 'Widget Creator Closed',
    WIDGET_CREATOR_CHART_TYPE_SELECTED: 'Widget Creator Chart Type Selected',
    WIDGET_CREATOR_DATA_SOURCE_ADDED: 'Widget Creator Data Source Added',
    WIDGET_CREATOR_ADV_REPORTING_TOGGLED: 'Widget Creator Advanced Reporting Toggled',
    DASHBOARD_EDIT_STARTED: 'Dashboard Edit Started',
    DASHBOARD_EDIT_SAVED: 'Dashboard Edit Saved',
    DASHBOARD_EDIT_EXITED: 'Dashboard Edit Exited',
    // Presentations
    PRESENTATION_DOWNLOADED: 'Presentation Downloaded',
    PRESENTATION_ADD_ALL_QUESTIONS: 'Presentation Add All Questions',
    PRESENTATION_SLIDE_CLONED: 'Presentation Slide Cloned',
    PRESENTATION_SLIDE_DELETED: 'Presentation Slide Deleted',
    PRESENTATION_CHART_DELETED: 'Presentation Chart Deleted',
    PRESENTATION_SECTION_ADDED: 'Presentation Section Added',
    PRESENTATION_CHART_FILTER_APPLIED: 'Presentation Chart Filter Applied',
    PRESENTATION_TEMPLATE_INSERTED: 'Presentation Template Inserted',
    PRESENTATION_SAVED_OPENED: 'Saved Presentation Opened',
    PRESENTATION_CREATED: 'Presentation Created',
    // Distribution
    EMAIL_SENT: 'Email Sent',
    EMAIL_TEST_SENT: 'Email Test Sent',
    // Reporting
    REPORT_OPENED: 'Report Opened',
    REPORT_RELOAD: 'Report Reload', // { survey_status_filter: { completed: boolean, dropped: boolean, screened: boolean, test: boolean }, filters_applied: { response, verbatim, campaign, date }}
    REPORT_EXPORTED: 'Report Exported', // { format }
    REPORT_CLOSED: 'Report Closed',
    REPORT_FILTER_QUICK: 'Report Quick Filter Action', // { action: open | close | apply, filters_applied: {response: boolean, verbatim: boolean }}
    REPORT_FILTER_MODAL: 'Report Normal Filter Action', // { action: open | close | apply, filters_applied: { response, verbatim, campaign, date}}
    REPORT_FILTER_CLEAR: 'Report All Filters Cleared',
    REPORT_CROSSTABS_OPENED: 'Report Crosstabs Opened',
    REPORT_CROSSTABS_RUN: 'Report Crosstabs Run', // { num_of_col_questions, num_of_row_questions, filters_applied: { response, verbatim, campaign, date}}
    REPORT_AI_SUMMARY_OPENED: 'Report AI Summary Opened',
    REPORT_AI_SUMMARY_REFRESH_ATTEMPTED: 'Report AI Summary Refresh Attempted',
    REPORT_AI_OPEN_TEXT_GENERATED: 'Report AI Open Text Generated',
    REPORT_BENCHMARKS_COMPARISONS_APPLIED: 'Report Benchmarks/Comparisons applied',
    // { benchmark_period: '36 months' | 'Long term', benchmark_count, comparison_count }
    REPORT_BENCHMARKS_COMPARISONS_OPENED: 'Report Benchmarks/Comparisons Opened',
    // Navigation
    PAGE_VIEWED: 'Page Viewed',
    // Session
    USER_LOGGED_IN: 'User Logged In',
    USER_LOGGED_OUT: 'User Logged Out',
}

export const FILTER_TYPES_MAP = {
    question: 'response',
    openEnded: 'verbatim',
    campaign: 'campaign',
    responseDate: 'date',
}

class MixpanelManager {
    constructor(mixpanel = mixpanelBrowser) {
        this.mixpanel = mixpanel
        this.initialized = false
    }

    init(token, config = {}) {
        try {
            this.mixpanel.init(token, config)
            this.initialized = true
        } catch (e) {
            console.error('MixpanelManager: init() failed', e)
        }
    }

    identify(userData, isExploriAdmin = false) {
        if (!this.guard('identify')) return

        try {
            this.mixpanel.identify(userData.userId)

            this.mixpanel.people.set({
                session_id: userData.sessionId,
                client_id: userData.clientId,
                user_role: userData.role,
                user_role_label: this.getRoleLabel(userData.role),
                explori_staff: userData.flags.isExploriStaff,
                explori_reseller: userData.isReseller,
                locale: userData.locale,
                timezone: userData.timezone,
                dashboard_status: userData.dashboardStatus,
                ...this.flattenFlags('flag', userData.flags),
            })

            this.mixpanel.register({
                client_id: userData.clientId,
                user_role: userData.role,
                explori_staff: isExploriAdmin,
                explori_reseller: userData.isReseller,
            })
        } catch (e) {
            console.error('MixpanelManager: identify() failed', e)
        }
    }

    setClientProperties(client) {
        if (!this.guard('setClientProperties')) return

        try {
            const clientFlags = client.flags ?? {}
            const industryCodes = (client.industry ?? []).map((i) => i.code)

            this.mixpanel.register({
                client_survey_types: client.surveyTypes ?? [],
                client_industries: industryCodes,
                client_timezone: client.timezone,
                client_country: client.country,
                client_dashboard_status: client.dashboardStatus,
                ...this.flattenFlags('client_flag', clientFlags),
            })
        } catch (e) {
            console.error('MixpanelManager: setClientProperties() failed', e)
        }
    }

    trackDashboardLoaded({
        dashboardVersion, dashboardId, dashboardName, oId,
        recordId, accessContext, surveyId, hasActiveFilters, activeFilterCount,
    } = {}) {
        this.track(MIXPANEL_EVENTS.DASHBOARD_LOADED, {
            dashboard_version: dashboardVersion,
            dashboard_id: dashboardId,
            dashboard_name: dashboardName,
            dashboard_type: OID_TYPE_MAP[oId],
            record_id: recordId,
            access_context: accessContext,
            survey_id: surveyId,
            has_active_filters: hasActiveFilters,
            active_filter_count: activeFilterCount,
            referrer: document.referrer || undefined,
            url: window.location.href,
        })
    }

    track(eventName, properties = {}) {
        if (!this.guard('track')) return

        try {
            this.mixpanel.track(eventName, properties)
        } catch (e) {
            console.error(`MixpanelManager: track("${eventName}") failed`, e)
        }
    }

    trackPageView(path, properties = {}) {
        this.track(MIXPANEL_EVENTS.PAGE_VIEWED, { path, ...properties })
    }

    trackLogin() {
        this.track(MIXPANEL_EVENTS.USER_LOGGED_IN)
    }

    trackLogout() {
        this.track(MIXPANEL_EVENTS.USER_LOGGED_OUT)
        this.reset()
    }

    // Call on logout to dissociate the current user
    reset() {
        if (!this.initialized) return

        try {
            this.mixpanel.reset()
        } catch (e) {
            console.error('MixpanelManager: reset() failed', e)
        }
    }

    guard(methodName) {
        if (!this.initialized) {
            console.warn(`MixpanelManager: cannot call ${methodName}() before init()`)
            return false
        }
        return true
    }

    getRoleLabel(role) {
        return ROLE_LABELS[role] ?? 'Unknown'
    }

    flattenFlags(prefix, flags = {}) {
        return Object.entries(flags).reduce((acc, [key, value]) => {
            acc[`${prefix}_${key}`] = value
            return acc
        }, {})
    }
}

/** Mixpanel: Survey Builder */
/* Defined here because survey builder doesn't have a dedicated store */
export const trackBuilder = (store, eventName, properties = {}) => {
    const { survey } = store

    store.mixpanel?.track(eventName, {
        survey_id: survey.id,
        product_id: survey.metadata.productInfo.productId,
        survey_type: survey.metadata.taxonomy.surveyType,
        ...properties,
    })
}

export const trackBuilderOpen = (store) => trackBuilder(store, MIXPANEL_EVENTS.SURVEY_BUILDER_OPENED)

export const trackBuilderClose = (store) => trackBuilder(store, MIXPANEL_EVENTS.SURVEY_BUILDER_CLOSED)

export const trackBuilderPageAdd = (store) => trackBuilder(store, MIXPANEL_EVENTS.SURVEY_BUILDER_PAGE_ADDED)

export const trackBuilderPageMenuAction = (store, properties) => (trackBuilder(store, MIXPANEL_EVENTS.SURVEY_BUILDER_PAGE_MENU_ACTION, properties))
// action: 'delete page' | 'preview page' | 'update redirect url' | 'update redirect delay' | 'remove redirect url' | 'toggle lock page' | 'toggle hide page' | 'open logic menu'
// value: boolean | num
// logic_type_menu?: 'page display/logic'

export const trackBuilderQuestionMenuAction = (store, properties) => (
    trackBuilder(store, MIXPANEL_EVENTS.SURVEY_BUILDER_QUESTION_MENU_ACTION, properties)
)
// action: 'delete question' | 'duplicate question' | 'toggle required' | 'toggle lock question' | 'toggle hide question' | 'inherit answer options' | 'map responses' | 'toggle ai coding'
// value: boolean
// logic_type_menu?: 'inherit' | 'question display/logic'

export const trackBuilderQuestionDrag = (store, from) => (
    trackBuilder(store, MIXPANEL_EVENTS.SURVEY_BUILDER_QUESTION_DRAGGED, { from })
    // from: 'explori' | 'company' | 'recommended' | 'mandatory'
)

export const trackBuilderQuestionMandatoryAutoAdd = (store) => (
    trackBuilder(store, MIXPANEL_EVENTS.SURVEY_BUILDER_QUESTION_MANDATORY_AUTO_ADDED))

export const trackBuilderQuestionCreateEdit = (store, properties) => (
    trackBuilder(store, MIXPANEL_EVENTS.SURVEY_BUILDER_QUESTION_CREATE_EDIT, properties)
)

export const trackBuilderLogicEdit = (store, properties) => (
    trackBuilder(store, MIXPANEL_EVENTS.SURVEY_BUILDER_LOGIC_EDITED, properties))

/** end Mixpanel: Survey Builder */

export { OID_TYPE_MAP }
export default MixpanelManager
